/**
 * @module utils/accessibility
 * @description Accessibility utilities for focus management and keyboard navigation
 *
 * Provides:
 * - Focus trap for modals and dialogs
 * - Keyboard navigation for menus
 * - Focus restoration when closing overlays
 *
 * @example
 * import { createFocusTrap, trapFocus } from '@/utils';
 *
 * // In a Svelte component
 * let modalElement: HTMLElement;
 * let releaseTrap: (() => void) | null = null;
 *
 * $: if (visible && modalElement) {
 *   releaseTrap = trapFocus(modalElement);
 * }
 *
 * function handleClose() {
 *   releaseTrap?.();
 *   visible = false;
 * }
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/** Selector for all focusable elements */
const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// =============================================================================
// FOCUS TRAP
// =============================================================================

/**
 * Get all focusable elements within a container
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const elements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
  return Array.from(elements).filter(
    (el) => el.offsetParent !== null // Filter out hidden elements
  );
}

/**
 * Trap focus within a container element
 *
 * @param container The element to trap focus within
 * @param options Configuration options
 * @returns A function to release the trap
 */
export function trapFocus(
  container: HTMLElement,
  options: {
    /** Focus the first element when trap is activated */
    autoFocus?: boolean;
    /** Restore focus to this element when trap is released */
    restoreFocus?: HTMLElement | null;
  } = {}
): () => void {
  const { autoFocus = true, restoreFocus = document.activeElement as HTMLElement } = options;

  const focusableElements = getFocusableElements(container);
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  // Auto-focus first element
  if (autoFocus && firstElement) {
    // Use setTimeout to ensure the element is rendered
    setTimeout(() => firstElement.focus(), 0);
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab') return;

    // Refresh focusable elements (in case they changed)
    const currentFocusable = getFocusableElements(container);
    if (currentFocusable.length === 0) return;

    const first = currentFocusable[0];
    const last = currentFocusable[currentFocusable.length - 1];

    if (event.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab: if on last element, wrap to first
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);

  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleKeydown);
    // Restore focus
    if (restoreFocus && typeof restoreFocus.focus === 'function') {
      restoreFocus.focus();
    }
  };
}

/**
 * Creates a focus trap that can be activated/deactivated
 */
export interface FocusTrap {
  activate: () => void;
  deactivate: () => void;
  isActive: () => boolean;
}

export function createFocusTrap(
  container: HTMLElement,
  options: {
    autoFocus?: boolean;
    restoreFocus?: HTMLElement | null;
  } = {}
): FocusTrap {
  let release: (() => void) | null = null;

  return {
    activate() {
      if (release) return;
      release = trapFocus(container, options);
    },
    deactivate() {
      if (release) {
        release();
        release = null;
      }
    },
    isActive() {
      return release !== null;
    },
  };
}

// =============================================================================
// MENU KEYBOARD NAVIGATION
// =============================================================================

/**
 * Handle keyboard navigation for a menu
 *
 * @param container The menu container element
 * @param itemSelector CSS selector for menu items
 * @returns A function to remove the event listener
 */
export function enableMenuKeyboardNavigation(
  container: HTMLElement,
  itemSelector: string = '[role="menuitem"]'
): () => void {
  let currentIndex = -1;

  function getMenuItems(): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(itemSelector)).filter(
      (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
    );
  }

  function focusItem(index: number) {
    const items = getMenuItems();
    if (items.length === 0) return;

    // Clamp index
    currentIndex = ((index % items.length) + items.length) % items.length;
    items[currentIndex].focus();
  }

  function handleKeydown(event: KeyboardEvent) {
    const items = getMenuItems();
    if (items.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(items.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (currentIndex >= 0 && items[currentIndex]) {
          items[currentIndex].click();
        }
        break;
    }
  }

  // Focus first item initially
  setTimeout(() => focusItem(0), 0);

  container.addEventListener('keydown', handleKeydown);

  return () => {
    container.removeEventListener('keydown', handleKeydown);
  };
}

// =============================================================================
// FOCUS MANAGEMENT
// =============================================================================

/**
 * Focus the first focusable element in a container
 */
export function focusFirst(container: HTMLElement): boolean {
  const focusable = getFocusableElements(container);
  if (focusable.length > 0) {
    focusable[0].focus();
    return true;
  }
  return false;
}

/**
 * Check if an element is currently focused or contains focus
 */
export function containsFocus(element: HTMLElement): boolean {
  return element.contains(document.activeElement);
}

// =============================================================================
// LIVE REGION
// =============================================================================

/**
 * Announce a message to screen readers
 *
 * @param message The message to announce
 * @param priority 'polite' (default) or 'assertive'
 */
export function announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
  const liveRegion = document.getElementById('a11y-live-region') || createLiveRegion();
  liveRegion.setAttribute('aria-live', priority);
  liveRegion.textContent = message;

  // Clear after announcement
  setTimeout(() => {
    liveRegion.textContent = '';
  }, 1000);
}

function createLiveRegion(): HTMLElement {
  const region = document.createElement('div');
  region.id = 'a11y-live-region';
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.style.cssText = `
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `;
  document.body.appendChild(region);
  return region;
}

// =============================================================================
// MODAL KEYBOARD HANDLING
// =============================================================================

/**
 * Configuration for modal keyboard handler
 */
export interface ModalKeyboardOptions {
  /** Handler called when Escape is pressed */
  onEscape?: () => void;
  /** Handler called when Enter is pressed */
  onEnter?: () => void;
  /** Condition to check before calling onEnter (e.g., form validation) */
  enterCondition?: () => boolean;
  /** Whether to use document-level listener (for context menus) or element-level */
  useDocumentListener?: boolean;
}

/**
 * Return type for modal keyboard handler
 */
export interface ModalKeyboardHandler {
  /** The keydown handler function to attach to elements */
  handleKeydown: (event: KeyboardEvent) => void;
  /** Attach document-level listener (call in onMount) */
  attach: () => void;
  /** Remove document-level listener (call in onDestroy) */
  detach: () => void;
}

/**
 * Creates a keyboard handler for modals with common shortcuts
 *
 * This centralizes the keyboard handling pattern used across modals:
 * - Escape: Close the modal
 * - Enter: Submit/confirm action (optional, with condition check)
 *
 * @param options Configuration for the keyboard handler
 * @returns Object with handler function and lifecycle methods
 *
 * @example
 * // For modals with Enter to submit
 * const keyboard = createModalKeyboardHandler({
 *   onEscape: handleClose,
 *   onEnter: handleSubmit,
 *   enterCondition: () => formIsValid
 * });
 *
 * // In template: on:keydown={keyboard.handleKeydown}
 *
 * @example
 * // For context menus with document-level listener
 * const keyboard = createModalKeyboardHandler({
 *   onEscape: hideMenu,
 *   useDocumentListener: true
 * });
 *
 * onMount(() => keyboard.attach());
 * onDestroy(() => keyboard.detach());
 */
export function createModalKeyboardHandler(options: ModalKeyboardOptions): ModalKeyboardHandler {
  const { onEscape, onEnter, enterCondition = () => true, useDocumentListener = false } = options;

  function handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        if (onEscape) {
          event.preventDefault();
          onEscape();
        }
        break;
      case 'Enter':
        if (onEnter && enterCondition()) {
          // Don't prevent default if inside a textarea
          if ((event.target as HTMLElement)?.tagName !== 'TEXTAREA') {
            event.preventDefault();
            onEnter();
          }
        }
        break;
    }
  }

  return {
    handleKeydown,
    attach() {
      if (useDocumentListener) {
        document.addEventListener('keydown', handleKeydown);
      }
    },
    detach() {
      if (useDocumentListener) {
        document.removeEventListener('keydown', handleKeydown);
      }
    },
  };
}

/**
 * Simplified function for modals that only need Escape handling
 * Commonly used for closing modals/menus
 *
 * @param onClose Function to call when Escape is pressed
 * @returns Keydown handler function
 *
 * @example
 * // Simple usage for close-only modals
 * const handleKeydown = escapeToClose(handleClose);
 * // In template: on:keydown={handleKeydown}
 */
export function escapeToClose(onClose: () => void): (event: KeyboardEvent) => void {
  return (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };
}

/**
 * Creates a document-level Escape key listener
 * Useful for context menus and overlays
 *
 * @param onClose Function to call when Escape is pressed
 * @returns Object with attach/detach methods for lifecycle management
 *
 * @example
 * const escapeListener = createEscapeListener(hideMenu);
 *
 * onMount(() => escapeListener.attach());
 * onDestroy(() => escapeListener.detach());
 */
export function createEscapeListener(onClose: () => void): {
  attach: () => void;
  detach: () => void;
} {
  const handler = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return {
    attach: () => document.addEventListener('keydown', handler),
    detach: () => document.removeEventListener('keydown', handler),
  };
}
